import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../core/i18n/hooks/use-language';
import { AUTH0_CLIENT_ID, AUTH0_DOMAIN } from '../../core/config';
import { useAnonymousMutation } from '../backend/hooks/use-anonymous-mutation';
import { useNotification } from '../notifications/hooks/use-notification';
import { buildTextNotification } from '../notifications/helpers/notification-factory';

const AUTH0_CONNECTION_TYPE = 'Username-Password-Authentication' as const;

type ChangePasswordBody = {
  client_id: string;
  email: string;
  connection: string;
};

type ChangePasswordResponse = {
  message?: string;
};

/**
 * These are mutations that communicate with Auth0 directly.
 */
const authUrls = {
  changePassword: () => `https://${AUTH0_DOMAIN}/dbconnections/change_password` as const,
};

export const useChangePasswordMutation = () => {
  const { t } = useTranslation();
  const pushNotification = useNotification();
  const currentLanguage = useLanguage();

  return useAnonymousMutation<ChangePasswordResponse, ChangePasswordBody>({
    method: 'POST',
    url: authUrls.changePassword(),
    options: {
      onSuccess: () => {
        pushNotification(
          buildTextNotification({
            text: t('auth.changePasswordRequest.successMessage'),
          }),
        );
      },
      onError: () => {
        pushNotification(
          buildTextNotification({
            text: t('auth.changePasswordRequest.errorMessage'),
          }),
        );
      },
    },
    bodyType: 'raw',
    language: currentLanguage,
  });
};

export const useChangePassword = () => {
  const { mutateAsync, isPending } = useChangePasswordMutation();
  return {
    mutateAsync: async (email: string) => {
      await mutateAsync({
        client_id: AUTH0_CLIENT_ID,
        email: email,
        connection: AUTH0_CONNECTION_TYPE,
      });
    },
    isPending,
  };
};
